// Plan entitlement layer cho GMS SaaS multi-tenant.
// TRỤC 1 (org mua gì) — độc lập với lib/permissions.ts (TRỤC 2: user được làm gì).
// Quy tắc vàng: feature bật ⇔ planAllows(plan, key) && roleCan(role, key).
// Nguồn map gói: docs/SAAS-MIGRATION-PLAN.md §3.2. Feature keys phải khớp FEATURES trong lib/permissions.ts.
//
// MATRIX ĐỘNG (super-admin sửa qua /admin/plans): map gói tĩnh bên dưới (CORE/PLUS/PRO) là MẶC ĐỊNH.
// Operator có thể override per-(featureKey × plan) → lưu root `system/planFeatures` → merge với mặc định.
// Cả server (tenant-auth.getPlanMatrix) lẫn client (tenant-context) đọc matrix đã merge.

import { FEATURES } from './permissions';

export type Plan = 'free' | 'host' | 'host_plus' | 'host_pro';

export const PLANS: Plan[] = ['free', 'host', 'host_plus', 'host_pro'];

export const PLAN_LABELS: Record<Plan, string> = {
  free:      'Miễn phí',
  host:      'Host',
  host_plus: 'Host Plus',
  host_pro:  'Host Pro',
};

export const PLAN_DESCRIPTIONS: Record<Plan, string> = {
  free:      'Tạo toà/căn + đăng ảnh/mô tả/tiện ích, trang giới thiệu /sale công khai — MIỄN PHÍ trọn đời (không OTA/booking/AI)',
  host:      'OTA sync 3 kênh + Direct, AI inbox 24/7, tin tự động, cổng khách',
  host_plus: 'Host + Giặt là, Làm phòng, Xe đưa đón, Sự cố',
  host_pro:  'Toàn bộ chức năng — điện/nước, kho, chấm công, quỹ, smart pricing, finance',
};

// ── Feature → gói (cộng dồn) ──────────────────────────────
// CORE: mọi gói đều có (hạ tầng + bán cốt lõi).
// PLUS_EXTRA: Host Plus thêm so với Host.
// PRO_EXTRA: Host Pro thêm so với Host Plus.
// Con số/feature có thể điều chỉnh — đồng bộ với SAAS-MIGRATION-PLAN.md khi đổi.

// FREE (gói miễn phí trọn đời): CHỈ dựng danh mục căn + trang giới thiệu /sale công khai.
// Subset của CORE (free ⊂ host). KHÔNG có OTA/booking/timeline/AI/chat/HDDH… (đó là lý do trả phí).
// `settings` = tạo toà/căn + upload ảnh/mô tả/tiện ích + bật hiện /sale; `sale_viewings` = nhận lịch xem căn.
const FREE: string[] = [
  'dashboard', 'rooms', 'settings', 'sale_viewings',
];

const CORE: string[] = [
  'dashboard', 'todos', 'rooms', 'timeline',
  'bookings', 'conversations', 'messages', 'guests', 'maintenance',
  'knowledge', 'templates', 'settings', 'admin', 'admin_car', 'sale_viewings',
  'hola', // module HOLA — gói nào cũng qua trục gói; tenant PHẢI có cờ holaEnabled mới dùng được (lib/hola-auth.ts)
];

const PLUS_EXTRA: string[] = [
  'housekeeping', 'laundry', 'supplies', 'incidents', 'car_payments',
  'admin_users', 'admin_roles', // RBAC động chỉ từ Host Plus trở lên
];

const PRO_EXTRA: string[] = [
  'tools', 'warehouse', 'water', 'attendance', 'fund',
  'contracts', 'pricing', 'finance', 'reports', 'performance',
  'recurring_tasks', // Việc định kỳ (30/10/2026, chốt gói Host Pro)
];

export const PLAN_FEATURES: Record<Plan, Set<string>> = {
  free:      new Set(FREE),
  host:      new Set(CORE),
  host_plus: new Set([...CORE, ...PLUS_EXTRA]),
  host_pro:  new Set([...CORE, ...PLUS_EXTRA, ...PRO_EXTRA]),
};

// ── Giới hạn định lượng per-gói ───────────────────────────
// -1 = không giới hạn. Tenant doc có thể override (tenants/{id}.limits).
// Host Plus = 5 toà / 50 căn (đã chốt 2026-06-27).

export const UNLIMITED = -1;

export interface PlanLimits {
  maxBuildings: number;
  maxRooms: number;
  maxStaff: number;
}

export type PlanLimitsMap = Record<Plan, PlanLimits>;

// MẶC ĐỊNH. Super-admin sửa qua /admin/plans → override lưu root system/planFeatures.limits.
export const PLAN_LIMITS: PlanLimitsMap = {
  // free = "nếm thử" trọn đời (chỉ dựng danh mục + /sale), luôn ≤ gói trả phí thấp nhất (host) để
  // thứ tự gói đơn điệu — free không được rộng hơn host ở bất kỳ trục nào.
  free:      { maxBuildings: 1,         maxRooms: 10,        maxStaff: 1         },
  host:      { maxBuildings: 1,         maxRooms: 10,        maxStaff: 3         },
  host_plus: { maxBuildings: 5,         maxRooms: 50,        maxStaff: 15        },
  host_pro:  { maxBuildings: UNLIMITED, maxRooms: UNLIMITED, maxStaff: UNLIMITED },
};

// Giới hạn album media /sale (P4S) theo gói. Ảnh đã resize (sharp) nên nhỏ → chỉ cap SỐ LƯỢNG.
// Video KHÔNG transcode (upload thô tối đa 150MB/file = tốn Storage) → CHỈ gói trả phí được đăng video.
export interface PlanMedia { allowVideo: boolean; maxPerEntity: number } // maxPerEntity: -1 = ∞
export const PLAN_MEDIA: Record<Plan, PlanMedia> = {
  free:      { allowVideo: false, maxPerEntity: 15        },
  host:      { allowVideo: true,  maxPerEntity: 40        },
  host_plus: { allowVideo: true,  maxPerEntity: 80        },
  host_pro:  { allowVideo: true,  maxPerEntity: UNLIMITED },
};

/** Merge override limit (Firestore) với mặc định, đủ mọi gói × 3 trường. */
export function mergePlanLimits(
  stored?: Partial<Record<Plan, Partial<PlanLimits>>>,
): PlanLimitsMap {
  return {
    free:      { ...PLAN_LIMITS.free,      ...stored?.free },
    host:      { ...PLAN_LIMITS.host,      ...stored?.host },
    host_plus: { ...PLAN_LIMITS.host_plus, ...stored?.host_plus },
    host_pro:  { ...PLAN_LIMITS.host_pro,  ...stored?.host_pro },
  };
}

// ── Helpers ───────────────────────────────────────────────

/** Gói này có quyền dùng feature không (TRỤC 1). KHÔNG thay roleCan (TRỤC 2). */
export function planAllows(plan: Plan, featureKey: string): boolean {
  return (PLAN_FEATURES[plan] ?? PLAN_FEATURES.host).has(featureKey);
}

/** Limit hiệu lực = override của tenant (nếu có) > limit của gói. */
export function effectiveLimits(plan: Plan, overrides?: Partial<PlanLimits>): PlanLimits {
  return { ...PLAN_LIMITS[plan], ...overrides };
}

/** count đã vượt limit chưa (limit -1 = vô hạn → không bao giờ vượt). */
export function exceedsLimit(limit: number, count: number): boolean {
  return limit !== UNLIMITED && count > limit;
}

export function isPlan(v: unknown): v is Plan {
  return v === 'free' || v === 'host' || v === 'host_plus' || v === 'host_pro';
}

// ── Matrix động (featureKey × plan) ───────────────────────
// Lưu root `system/planFeatures` (toàn cục, không per-tenant — đây là ĐỊNH NGHĨA SẢN PHẨM SaaS).
// Chỉ super-admin (operator gardenia) sửa được. Client đọc được (rules: system/{doc} read if signedIn).

export type PlanFeatureMatrix = Record<string, Record<Plan, boolean>>;

// ── Sub-feature (chức năng nhỏ trong 1 trang, vd các tab Báo cáo) ──────────
// Key dạng `parent.sub` (vd 'reports.sla'). Gói bật/tắt từng sub ở /admin/plans.
// Trang tiêu thụ gọi planAllows('reports.sla') để ẩn/hiện tab. Mặc định = theo gói của PARENT.
export interface SubFeatureDef { key: string; label: string; parent: string; }

export const SUB_FEATURES: SubFeatureDef[] = [
  { parent: 'reports', key: 'reports.revenue', label: 'Doanh thu' },
  { parent: 'reports', key: 'reports.svc_fee', label: 'Bảng thu DV' },
  { parent: 'reports', key: 'reports.service', label: 'Chi phí dịch vụ' },
  { parent: 'reports', key: 'reports.debt',    label: 'Công nợ' },
  { parent: 'reports', key: 'reports.surcharge', label: 'Phụ thu' },
  { parent: 'reports', key: 'reports.performance', label: 'Hiệu suất (lấp đầy/doanh thu)' },
  { parent: 'reports', key: 'reports.profit', label: 'Lợi nhuận theo phòng/tòa' },
  { parent: 'reports', key: 'reports.rental_compare', label: 'So sánh Dài hạn/Ngắn hạn' },
  { parent: 'reports', key: 'reports.reviews', label: 'Đánh giá khách' },
  { parent: 'reports', key: 'reports.sla',     label: 'SLA Chat' },
  { parent: 'reports', key: 'reports.ai',      label: 'AI (usage/chi phí)' },
  { parent: 'reports', key: 'reports.chatai',  label: 'Chat AI (log)' },
  { parent: 'reports', key: 'reports.automation', label: 'Tự động hoá chăm sóc' },
  { parent: 'reports', key: 'reports.ap_performance', label: 'Hiệu suất Auto-Pricer' },
  { parent: 'reports', key: 'reports.usage',   label: 'Hành vi dùng (UX)' },
  { parent: 'reports', key: 'reports.portal',  label: 'Cổng khách (insight)' },
  { parent: 'reports', key: 'reports.door_access', label: 'Ra vào toà (khoá TTLock)' },
  { parent: 'reports', key: 'reports.sale_funnel', label: 'Đặt phòng /sale (funnel)' },
  { parent: 'reports', key: 'reports.p4s',     label: 'P4S/Sale (lịch xem căn)' },
  { parent: 'reports', key: 'reports.errors',  label: 'Lỗi hệ thống' },
  { parent: 'reports', key: 'reports.flightapi', label: 'Quota API tra cứu chuyến bay' },
  // Pricing — tự động hoá giá (premium); Smart Push cơ bản KHÔNG gate (luôn theo parent 'pricing').
  { parent: 'pricing', key: 'pricing.auto_pricer', label: 'Auto-Pricer (tự đổi giá)' },
  { parent: 'pricing', key: 'pricing.seasonal',    label: 'Bộ quy tắc giá theo mùa' },
  // Housekeeping — dashboard analytics; Danh sách + Lịch tuần KHÔNG gate.
  { parent: 'housekeeping', key: 'housekeeping.dash', label: 'Dashboard Hiệu suất' },
  // Chấm công — báo cáo lương 2 pháp nhân (niche).
  { parent: 'attendance', key: 'attendance.bcc', label: 'BCC BC (báo cáo lương)' },
];

export const SUB_FEATURES_BY_PARENT: Record<string, SubFeatureDef[]> = SUB_FEATURES.reduce(
  (acc, s) => { (acc[s.parent] ??= []).push(s); return acc; },
  {} as Record<string, SubFeatureDef[]>,
);

/** Matrix MẶC ĐỊNH: FEATURES theo map gói tĩnh + SUB_FEATURES theo gói của parent. */
export const DEFAULT_PLAN_MATRIX: PlanFeatureMatrix = Object.fromEntries([
  ...FEATURES.map(f => [f.key, {
    free:      PLAN_FEATURES.free.has(f.key),
    host:      PLAN_FEATURES.host.has(f.key),
    host_plus: PLAN_FEATURES.host_plus.has(f.key),
    host_pro:  PLAN_FEATURES.host_pro.has(f.key),
  }] as const),
  ...SUB_FEATURES.map(sf => [sf.key, {
    free:      PLAN_FEATURES.free.has(sf.parent),
    host:      PLAN_FEATURES.host.has(sf.parent),
    host_plus: PLAN_FEATURES.host_plus.has(sf.parent),
    host_pro:  PLAN_FEATURES.host_pro.has(sf.parent),
  }] as const),
]);

/** Merge override (Firestore) với mặc định. Mặc định là nguồn danh sách key (không nhận key lạ).
 *
 *  ⚠️ Merge PER-PLAN-FIELD, không phải per-row: `system/planFeatures.matrix` được ghi từ TRƯỚC khi
 *  gói `free` tồn tại (khi `/admin/plans` chỉ có 3 cột Host/Plus/Pro) — MỌI row cũ đều THIẾU hẳn field
 *  `free` (không phải `free:false`, mà là key `free` không tồn tại trong object). Nếu coi "row đã lưu"
 *  ⇒ dùng nguyên `!!s.free` (bug thật 2026-09-23, phát hiện khi hạ tenant Areca về free): `!!undefined`
 *  = `false` y hệt `!!false`, không phân biệt được "operator từng chỉnh sửa row này và chủ ý khoá free"
 *  với "row này chưa BAO GIỜ có khái niệm free" ⇒ XOÁ SẠCH quyền free ở TOÀN BỘ 44/44 featureKey đã
 *  từng bị sửa qua `/admin/plans` (kể cả `dashboard`/`rooms`/`settings`/`sale_viewings` — đúng 4 feature
 *  gói free ĐƯỢC PHÉP theo thiết kế `FREE` ở trên) — signup tự phục vụ tạo tenant free xong vào thẳng
 *  màn khoá 100%. Sửa: dùng `'plan' in s` để phân biệt "thiếu field" (fallback `def[key]`, đúng ý định
 *  gốc) với "field có mặt, dù giá trị false" (giữ nguyên lựa chọn operator). */
export function mergePlanMatrix(
  stored: Partial<PlanFeatureMatrix> | undefined,
  def: PlanFeatureMatrix = DEFAULT_PLAN_MATRIX,
): PlanFeatureMatrix {
  const result: PlanFeatureMatrix = {};
  for (const key of Object.keys(def)) {
    const s = stored?.[key];
    result[key] = s
      ? {
          free:      'free'      in s ? !!s.free      : def[key].free,
          host:      'host'      in s ? !!s.host      : def[key].host,
          host_plus: 'host_plus' in s ? !!s.host_plus : def[key].host_plus,
          host_pro:  'host_pro'  in s ? !!s.host_pro  : def[key].host_pro,
        }
      : { ...def[key] };
  }
  return result;
}

/** Gói `plan` có feature `key` không, theo matrix động.
 *  Key chưa có trong matrix (matrix null/đang tải, hoặc sub-feature mới) → fallback tĩnh:
 *  sub-feature (`parent.sub`) theo gói của PARENT; feature thường theo map gói → KHÔNG ẩn nhầm khi tải. */
export function planMatrixAllows(
  matrix: PlanFeatureMatrix | null | undefined,
  plan: Plan,
  featureKey: string,
): boolean {
  const row = matrix?.[featureKey];
  if (row) return !!row[plan];
  const base = featureKey.includes('.') ? featureKey.split('.')[0] : featureKey;
  return planAllows(plan, base);
}

/** Danh sách featureKey gói `plan` được phép, theo matrix. */
export function allowedFeaturesForPlan(matrix: PlanFeatureMatrix, plan: Plan): string[] {
  return Object.keys(matrix).filter(k => matrix[k][plan]);
}

// ── Module taxonomy cho trang /admin/plans ────────────────
// Gom 100% featureKey (FEATURES) vào module theo cấu trúc gms-features.html.
// planModulesWithCoverage() tự thêm module "Khác" nếu có featureKey mới chưa phân nhóm → luôn phủ đủ.

export interface PlanModule {
  key: string;
  label: string;
  desc: string;
  features: string[]; // featureKey thuộc module
}

export const PLAN_MODULES: PlanModule[] = [
  { key: 'dieu-hanh', label: 'Tổng quan & Điều hành',   desc: 'Bảng điều khiển, việc cần làm',                       features: ['dashboard', 'todos'] },
  { key: 'so-do',     label: 'Sơ đồ phòng & Timeline',  desc: 'Room map, Gantt multi-room, căn ảo',                  features: ['rooms', 'timeline'] },
  { key: 'booking',   label: 'Quản lý Booking',         desc: 'CRUD booking, check-in/out, khoá phòng',              features: ['bookings', 'maintenance'] },
  { key: 'khach',     label: 'Quản lý Khách',           desc: 'Hồ sơ khách, dedup, lịch sử lưu trú',                 features: ['guests'] },
  { key: 'tin-nhan',  label: 'Tin nhắn & Hội thoại',    desc: 'Chat Airbnb, AI auto-reply, tin tự động, tri thức',   features: ['conversations', 'messages', 'knowledge', 'templates'] },
  { key: 'nhan-su',   label: 'Làm phòng & Nhân sự',     desc: 'Housekeeping, chấm công, hiệu suất NV',               features: ['housekeeping', 'recurring_tasks', 'attendance', 'performance'] },
  { key: 'kho',       label: 'Kho & Vật tư',            desc: 'Giặt là, vật tư, CCDC, kho đồ vải, nước uống',        features: ['laundry', 'supplies', 'tools', 'warehouse', 'water'] },
  { key: 'su-co',     label: 'Sự cố vận hành',          desc: 'Báo cáo & xử lý sự cố, nhà thầu',                     features: ['incidents', 'hola'] },
  { key: 'xe',        label: 'Xe đưa đón',              desc: 'Chi phí xe MTT, đối soát, thanh toán',                features: ['car_payments'] },
  { key: 'gia',       label: 'Giá & Smart Pricing',     desc: 'Bảng giá, auto-pricer, bộ quy tắc giá theo mùa',      features: ['pricing'] },
  { key: 'tai-chinh', label: 'Tài chính & Báo cáo',     desc: 'Hợp đồng, thu chi, quỹ lễ tân, báo cáo',              features: ['contracts', 'finance', 'fund', 'reports'] },
  { key: 'quan-tri',  label: 'Cấu hình & Quản trị',     desc: 'Cài đặt, quản trị, phân quyền, quản lý role',         features: ['settings', 'admin', 'admin_users', 'admin_roles'] },
];

/** PLAN_MODULES + module "Khác" cho mọi featureKey chưa phân nhóm (đảm bảo phủ 100%). */
export function planModulesWithCoverage(): PlanModule[] {
  const covered = new Set(PLAN_MODULES.flatMap(m => m.features));
  const extra = FEATURES.map(f => f.key).filter(k => !covered.has(k));
  return extra.length
    ? [...PLAN_MODULES, { key: 'khac', label: 'Khác', desc: 'Tính năng chưa phân nhóm', features: extra }]
    : PLAN_MODULES;
}
